<template>
    <div class="designer-page animated fadeIn">
        <div class="modern-dashboard-card">
            <div class="card-header-modern">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <b-button variant="link" class="text-white p-0 mr-3" @click="goBack">
                            <i class="fa fa-arrow-left fa-lg"></i>
                        </b-button>
                        <div class="header-icon-box mr-3">
                            <i class="fa fa-globe text-white"></i>
                        </div>
                        <div>
                            <h5 class="mb-0 text-white font-w700">RSVP Landing Page Designer</h5>
                            <small class="text-white-op-70">Design the page invitees see when they click their RSVP link</small>
                        </div>
                    </div>
                    <div class="d-flex">
                        <b-button variant="light" size="sm" class="mr-2" @click="previewDesign">
                            <i class="fa fa-eye mr-1"></i> Preview
                        </b-button>
                        <b-button variant="success" size="sm" @click="saveDesign" :disabled="saving">
                            <i class="fa fa-save mr-1"></i> {{ saving ? 'Saving...' : 'Save' }}
                        </b-button>
                    </div>
                </div>
            </div>

            <div class="card-body-modern">
                <div v-if="showTemplateGallery" class="template-gallery p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h5 class="font-w700 mb-1">Choose a Template</h5>
                            <p class="text-muted small mb-0">Pick a starting point or start from scratch</p>
                        </div>
                        <b-button variant="outline-primary" size="sm" class="btn-pill" @click="startFromScratch">
                            <i class="fa fa-plus mr-1"></i> Start from Scratch
                        </b-button>
                    </div>
                    <div class="template-grid">
                        <div
                            v-for="tpl in templates"
                            :key="tpl.id"
                            class="template-card"
                            @click="selectTemplate(tpl)"
                        >
                            <div class="template-preview" :style="tpl.previewStyle">
                                <div class="template-preview-content" v-html="tpl.previewHtml"></div>
                            </div>
                            <div class="template-info p-2">
                                <div class="font-w600 small">{{ tpl.name }}</div>
                                <div class="text-muted" style="font-size: 0.7rem;">{{ tpl.style }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="editor-container">
                    <div class="editor-toolbar p-2 d-flex align-items-center border-bottom">
                        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showTemplateGallery = true">
                            <i class="fa fa-th mr-1"></i> Templates
                        </b-button>
                        <small class="text-muted">
                            Variables: <code v-for="v in templateVars" :key="v" class="mr-1" v-text="'{{' + v + '}}'"></code>
                        </small>
                    </div>
                    <PageBuilder
                        ref="editor"
                        :value="editorHtml"
                        mode="page"
                        @input="onEditorChange"
                    />
                </div>
            </div>
        </div>

        <b-modal v-model="showPreview" title="RSVP Landing Page Preview" size="xl" hide-footer>
            <div class="preview-frame" v-html="renderedPreview"></div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
import popupMixins from '@/mixins/popupMixins'
import PageBuilder from '@/components/PageBuilder/PageBuilder.vue'

function v2Admin(path) {
    const v1Base = axios.defaults.baseURL || ''
    return v1Base.replace(/\/api\/v1\/?$/, '/api/v2/admin/') + path
}

const RSVP_TEMPLATES = [
    {
        id: 'keynote-hero',
        name: 'Keynote Hero',
        style: 'Dark / Bold',
        previewStyle: { background: '#0a0a0a' },
        previewHtml: '<div style="text-align:center;padding:12px;"><div style="font-size:5px;color:#999;text-transform:uppercase;letter-spacing:1px;">COMPANY</div><div style="font-size:13px;font-weight:900;color:#fff;margin:4px 0;">EVENT NAME</div><div style="font-size:6px;color:#e74c3c;">You\'re Invited</div><div style="display:flex;justify-content:center;gap:4px;margin-top:6px;"><div style="background:#22c55e;color:#fff;padding:2px 6px;border-radius:2px;font-size:5px;">Accept</div><div style="border:1px solid #ef4444;color:#ef4444;padding:2px 6px;border-radius:2px;font-size:5px;">Decline</div></div></div>',
        html: `<div style="min-height:100vh;background:linear-gradient(rgba(0,0,0,0.65),rgba(0,0,0,0.8)),url('{{flyer_url}}') center/cover no-repeat;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#fff;overflow-x:hidden;">
<div style="max-width:900px;margin:0 auto;padding:60px 24px 40px;">
<div style="text-align:center;margin-bottom:60px;">
<p style="font-size:0.85rem;text-transform:uppercase;letter-spacing:6px;color:rgba(255,255,255,0.5);margin:0 0 30px;">{{company_name}}</p>
<h1 style="font-size:clamp(2.5rem,6vw,4.5rem);font-weight:900;margin:0 0 24px;line-height:1.1;text-shadow:0 4px 20px rgba(0,0,0,0.5);">{{event_name}}</h1>
<div style="display:flex;align-items:center;justify-content:center;gap:16px;flex-wrap:wrap;font-size:1.05rem;color:rgba(255,255,255,0.8);margin-bottom:16px;">
<span>{{event_date}}</span>
<span style="color:#e74c3c;font-size:0.6rem;">&#9679;</span>
<span>{{venue}}</span>
</div>
<p style="font-size:0.95rem;color:rgba(255,255,255,0.5);margin:0;">{{address}}</p>
</div>
<div style="max-width:700px;margin:0 auto 50px;text-align:center;">
<p style="font-size:1.1rem;line-height:1.8;color:rgba(255,255,255,0.7);margin:0;">{{event_description}}</p>
</div>
<div style="background:rgba(255,255,255,0.06);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.1);border-radius:20px;padding:48px 40px;max-width:650px;margin:0 auto 40px;text-align:center;">
<p style="font-size:1.3rem;color:#e74c3c;font-weight:600;margin:0 0 8px;">You're Invited</p>
<h2 style="font-size:clamp(1.8rem,4vw,2.8rem);font-weight:800;margin:0 0 32px;">{{invitee_name}}</h2>
<div data-rsvp-action style="padding:24px 0;">
<p style="font-size:1rem;color:rgba(255,255,255,0.5);margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
</div>
<div style="display:flex;justify-content:center;gap:40px;flex-wrap:wrap;margin-bottom:40px;text-align:center;">
<div><div style="font-size:2rem;font-weight:800;color:#e74c3c;">500+</div><div style="font-size:0.8rem;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:2px;">Attendees</div></div>
<div><div style="font-size:2rem;font-weight:800;color:#e74c3c;">20+</div><div style="font-size:0.8rem;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:2px;">Speakers</div></div>
<div><div style="font-size:2rem;font-weight:800;color:#e74c3c;">4</div><div style="font-size:0.8rem;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:2px;">Tracks</div></div>
</div>
<div style="text-align:center;padding:20px 0;">
<p style="font-size:0.9rem;color:#e74c3c;font-weight:600;">RSVP Deadline: {{rsvp_deadline}}</p>
</div>
</div>
</div>`
    },
    {
        id: 'executive-summit',
        name: 'Executive Summit',
        style: 'Gradient Glass',
        previewStyle: { background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
        previewHtml: '<div style="text-align:center;padding:12px;"><div style="font-size:5px;color:#a78bfa;letter-spacing:1px;">COMPANY</div><div style="width:80%;height:30px;background:rgba(255,255,255,0.1);border-radius:4px;margin:6px auto;display:flex;align-items:center;justify-content:center;"><span style="font-size:5px;color:#888;">FLYER</span></div><div style="font-size:10px;font-weight:700;color:#fff;">Event Name</div><div style="background:rgba(255,255,255,0.08);border-radius:4px;padding:4px;margin-top:4px;"><div style="font-size:5px;color:#a78bfa;">RSVP</div></div></div>',
        html: `<div style="min-height:100vh;background:linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#fff;overflow-x:hidden;">
<div style="max-width:800px;margin:0 auto;padding:50px 24px;">
<div style="text-align:center;margin-bottom:12px;">
<p style="font-size:0.8rem;text-transform:uppercase;letter-spacing:5px;color:#a78bfa;margin:0;">{{company_name}}</p>
<div style="width:60px;height:1px;background:linear-gradient(90deg,transparent,#a78bfa,transparent);margin:16px auto 0;"></div>
</div>
<div style="margin:30px auto 36px;text-align:center;">
<img src="{{flyer_url}}" alt="Event Banner" style="width:100%;max-width:750px;border-radius:16px;box-shadow:0 20px 60px rgba(167,139,250,0.2);display:block;margin:0 auto;" />
</div>
<h1 style="font-size:clamp(2rem,5vw,3.2rem);font-weight:800;text-align:center;margin:0 0 12px;">{{event_name}}</h1>
<p style="text-align:center;font-size:1rem;color:rgba(255,255,255,0.6);margin:0 0 36px;">{{event_date}} &mdash; {{event_end_date}}</p>
<div style="background:rgba(255,255,255,0.07);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.12);border-radius:20px;padding:40px;max-width:650px;margin:0 auto 36px;">
<div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:20px;">
<span style="color:#a78bfa;font-size:1.1rem;margin-top:2px;">&#9673;</span>
<div><p style="margin:0;font-weight:600;font-size:1rem;">Venue</p><p style="margin:4px 0 0;color:rgba(255,255,255,0.6);font-size:0.95rem;">{{venue}}, {{address}}</p></div>
</div>
<div style="width:100%;height:1px;background:rgba(255,255,255,0.08);margin:0 0 20px;"></div>
<p style="font-size:1rem;line-height:1.8;color:rgba(255,255,255,0.7);margin:0 0 24px;">{{event_description}}</p>
<div style="width:100%;height:1px;background:rgba(255,255,255,0.08);margin:0 0 24px;"></div>
<p style="font-size:1.1rem;margin:0 0 6px;text-align:center;">Dear <span style="color:#a78bfa;font-weight:700;">{{invitee_name}}</span>,</p>
<p style="font-size:0.95rem;color:rgba(255,255,255,0.6);text-align:center;margin:0 0 28px;">We would be honoured to have you join us at this event.</p>
<div data-rsvp-action style="text-align:center;padding:20px 0;border:1px dashed rgba(167,139,250,0.3);border-radius:12px;">
<p style="font-size:1rem;color:rgba(255,255,255,0.4);margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
</div>
<div style="text-align:center;padding:20px 0;">
<p style="font-size:0.85rem;color:#a78bfa;font-weight:600;">Please respond by {{rsvp_deadline}}</p>
</div>
</div>
</div>`
    },
    {
        id: 'flyer-showcase',
        name: 'Flyer Showcase',
        style: 'Image-Forward',
        previewStyle: { background: '#f0f0f0' },
        previewHtml: '<div style="text-align:center;padding:0;"><div style="background:#ccc;height:50px;display:flex;align-items:center;justify-content:center;"><span style="font-size:6px;color:#888;">FLYER IMAGE</span></div><div style="background:#fff;border-radius:6px 6px 0 0;margin-top:-6px;padding:8px;position:relative;"><div style="font-size:10px;font-weight:700;color:#333;">Event</div><div style="font-size:5px;color:#888;">Details</div><div style="display:flex;justify-content:center;gap:3px;margin-top:4px;"><div style="background:#22c55e;padding:1px 4px;border-radius:1px;font-size:4px;color:#fff;">Accept</div><div style="border:1px solid #ef4444;padding:1px 4px;border-radius:1px;font-size:4px;color:#ef4444;">Decline</div></div></div></div>',
        html: `<div style="min-height:100vh;background:#f4f5f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow-x:hidden;">
<div style="width:100%;height:50vh;min-height:350px;overflow:hidden;">
<img src="{{flyer_url}}" alt="Event Banner" style="width:100%;height:100%;object-fit:cover;display:block;" />
</div>
<div style="max-width:750px;margin:-60px auto 0;padding:0 24px 50px;position:relative;z-index:1;">
<div style="background:#fff;border-radius:24px;box-shadow:0 10px 50px rgba(0,0,0,0.12);padding:48px 40px;overflow:hidden;">
<p style="font-size:0.75rem;text-transform:uppercase;letter-spacing:4px;color:#6366f1;margin:0 0 12px;font-weight:600;">{{company_name}}</p>
<h1 style="font-size:clamp(1.8rem,4vw,2.8rem);font-weight:800;color:#111;margin:0 0 20px;line-height:1.2;">{{event_name}}</h1>
<div style="display:flex;flex-wrap:wrap;gap:20px;margin-bottom:24px;">
<div style="display:flex;align-items:center;gap:8px;"><span style="color:#6366f1;font-size:1.1rem;">&#128197;</span><span style="color:#555;font-size:0.95rem;">{{event_date}}</span></div>
<div style="display:flex;align-items:center;gap:8px;"><span style="color:#6366f1;font-size:1.1rem;">&#128205;</span><span style="color:#555;font-size:0.95rem;">{{venue}}</span></div>
<div style="display:flex;align-items:center;gap:8px;"><span style="color:#6366f1;font-size:1.1rem;">&#127968;</span><span style="color:#555;font-size:0.95rem;">{{address}}</span></div>
</div>
<p style="font-size:1rem;line-height:1.8;color:#666;margin:0 0 32px;">{{event_description}}</p>
<div style="background:#f8f7ff;border-radius:16px;padding:32px;margin-bottom:32px;text-align:center;">
<p style="font-size:0.9rem;color:#888;margin:0 0 6px;">This invitation is for</p>
<h3 style="font-size:1.6rem;font-weight:700;color:#6366f1;margin:0 0 4px;">{{invitee_name}}</h3>
<p style="font-size:0.85rem;color:#aaa;margin:0;">{{invitee_email}}</p>
</div>
<div data-rsvp-action style="text-align:center;padding:28px 0;background:#fafafa;border-radius:12px;margin-bottom:24px;">
<p style="font-size:1rem;color:#aaa;margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
<p style="text-align:center;font-size:0.85rem;color:#999;margin:0;">Please respond by <span style="color:#6366f1;font-weight:600;">{{rsvp_deadline}}</span></p>
</div>
</div>
</div>`
    },
    {
        id: 'gala-night',
        name: 'Gala Night',
        style: 'Gold Luxury',
        previewStyle: { background: '#080808' },
        previewHtml: '<div style="text-align:center;padding:10px;"><div style="border:1px solid rgba(201,168,76,0.3);padding:10px 8px;position:relative;"><div style="font-size:8px;color:#c9a84c;">&#9830;</div><div style="font-size:5px;color:#c9a84c;font-style:italic;">You Are Invited</div><div style="font-size:10px;font-weight:700;color:#fff;margin:3px 0;">EVENT</div><div style="width:20px;height:1px;background:#c9a84c;margin:3px auto;"></div><div style="font-size:5px;color:#888;">Date & Venue</div><div style="font-size:8px;color:#c9a84c;">&#9830;</div></div></div>',
        html: `<div style="min-height:100vh;background:#080808;font-family:Georgia,'Times New Roman',serif;color:#fff;overflow-x:hidden;">
<div style="max-width:750px;margin:0 auto;padding:60px 24px;">
<div style="border:2px solid rgba(201,168,76,0.4);padding:60px 40px;position:relative;">
<div style="border:1px solid rgba(201,168,76,0.2);position:absolute;top:8px;left:8px;right:8px;bottom:8px;pointer-events:none;"></div>
<div style="position:absolute;top:-14px;left:50%;transform:translateX(-50%);background:#080808;padding:0 20px;"><span style="color:#c9a84c;font-size:1.5rem;">&#9830;</span></div>
<div style="text-align:center;position:relative;z-index:1;">
<p style="font-size:0.8rem;text-transform:uppercase;letter-spacing:6px;color:rgba(201,168,76,0.6);margin:0 0 24px;">{{company_name}}</p>
<p style="font-size:1.2rem;color:#c9a84c;font-style:italic;margin:0 0 20px;">You Are Cordially Invited</p>
<h1 style="font-size:clamp(2rem,5vw,3.5rem);font-weight:400;color:#fff;margin:0 0 20px;line-height:1.2;">{{event_name}}</h1>
<div style="width:100px;height:1px;background:linear-gradient(90deg,transparent,#c9a84c,transparent);margin:0 auto 24px;"></div>
<div style="margin-bottom:24px;">
<p style="font-size:1rem;color:#c9a84c;margin:0 0 6px;">{{event_date}}</p>
<p style="font-size:1rem;color:#c9a84c;margin:0 0 6px;">to {{event_end_date}}</p>
<p style="font-size:0.95rem;color:rgba(255,255,255,0.6);margin:0 0 4px;">{{venue}}</p>
<p style="font-size:0.85rem;color:rgba(255,255,255,0.4);margin:0;">{{address}}</p>
</div>
<div style="width:60px;height:1px;background:linear-gradient(90deg,transparent,#c9a84c,transparent);margin:0 auto 24px;"></div>
<p style="font-size:1rem;line-height:1.8;color:rgba(255,255,255,0.55);margin:0 0 32px;max-width:550px;margin-left:auto;margin-right:auto;">{{event_description}}</p>
<div style="width:60px;height:1px;background:linear-gradient(90deg,transparent,#c9a84c,transparent);margin:0 auto 28px;"></div>
<p style="font-size:1rem;color:rgba(255,255,255,0.6);margin:0 0 6px;">Prepared for</p>
<h2 style="font-size:clamp(1.5rem,3vw,2.2rem);font-weight:400;color:#c9a84c;margin:0 0 36px;font-style:italic;">{{invitee_name}}</h2>
<div data-rsvp-action style="border:1px solid rgba(201,168,76,0.3);border-radius:8px;padding:28px;margin-bottom:28px;">
<p style="font-size:1rem;color:rgba(201,168,76,0.4);margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
<p style="font-size:0.85rem;color:#c9a84c;margin:0;">Kindly respond by {{rsvp_deadline}}</p>
</div>
<div style="position:absolute;bottom:-14px;left:50%;transform:translateX(-50%);background:#080808;padding:0 20px;"><span style="color:#c9a84c;font-size:1.5rem;">&#9830;</span></div>
</div>
</div>
</div>`
    },
    {
        id: 'conference-pro',
        name: 'Conference Pro',
        style: 'Tech / Modern',
        previewStyle: { background: '#0f172a' },
        previewHtml: '<div style="padding:0;"><div style="height:4px;background:linear-gradient(90deg,#06b6d4,#3b82f6,#8b5cf6);"></div><div style="padding:8px;text-align:center;"><div style="font-size:11px;font-weight:800;color:#fff;">EVENT</div><div style="font-size:5px;color:#06b6d4;">Date & Venue</div></div><div style="background:#fff;padding:8px;"><div style="display:flex;gap:4px;"><div style="flex:1;"><div style="font-size:5px;color:#333;">Details</div></div><div style="flex:1;background:#eee;height:20px;border-radius:2px;display:flex;align-items:center;justify-content:center;"><span style="font-size:4px;color:#888;">IMG</span></div></div></div></div>',
        html: `<div style="min-height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow-x:hidden;">
<div style="height:4px;background:linear-gradient(90deg,#06b6d4,#3b82f6,#8b5cf6);"></div>
<div style="background:#0f172a;padding:50px 24px 60px;text-align:center;">
<p style="font-size:0.8rem;text-transform:uppercase;letter-spacing:4px;color:#06b6d4;margin:0 0 16px;font-weight:600;">{{company_name}}</p>
<h1 style="font-size:clamp(2rem,5vw,3.5rem);font-weight:800;color:#fff;margin:0 0 16px;line-height:1.15;">{{event_name}}</h1>
<div style="display:flex;align-items:center;justify-content:center;gap:16px;flex-wrap:wrap;font-size:0.95rem;color:rgba(255,255,255,0.7);">
<span>{{event_date}}</span><span style="color:#06b6d4;">|</span><span>{{venue}}</span>
</div>
</div>
<div style="background:#fff;padding:50px 24px;">
<div style="max-width:950px;margin:0 auto;display:flex;gap:40px;flex-wrap:wrap;">
<div style="flex:1;min-width:280px;">
<h3 style="font-size:1.3rem;font-weight:700;color:#0f172a;margin:0 0 20px;">Event Details</h3>
<div style="margin-bottom:16px;display:flex;align-items:flex-start;gap:10px;">
<span style="color:#06b6d4;font-size:1rem;margin-top:2px;">&#128197;</span>
<div><p style="margin:0;font-weight:600;color:#333;font-size:0.95rem;">Date & Time</p><p style="margin:4px 0 0;color:#666;font-size:0.9rem;">{{event_date}} to {{event_end_date}}</p></div>
</div>
<div style="margin-bottom:16px;display:flex;align-items:flex-start;gap:10px;">
<span style="color:#06b6d4;font-size:1rem;margin-top:2px;">&#128205;</span>
<div><p style="margin:0;font-weight:600;color:#333;font-size:0.95rem;">Location</p><p style="margin:4px 0 0;color:#666;font-size:0.9rem;">{{venue}}<br>{{address}}</p></div>
</div>
<div style="width:100%;height:1px;background:#e5e7eb;margin:20px 0;"></div>
<p style="font-size:0.95rem;line-height:1.8;color:#555;margin:0 0 28px;">{{event_description}}</p>
<div style="background:#f0fdfa;border-left:4px solid #06b6d4;border-radius:0 8px 8px 0;padding:20px;margin-bottom:24px;">
<p style="margin:0 0 4px;font-weight:600;color:#0f172a;">Hello, {{invitee_name}}</p>
<p style="margin:0;font-size:0.9rem;color:#666;">You have been personally invited to attend this event.</p>
</div>
<div data-rsvp-action style="padding:28px;background:#f8fafc;border-radius:12px;text-align:center;border:2px dashed #e2e8f0;">
<p style="font-size:1rem;color:#94a3b8;margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
</div>
<div style="flex:1;min-width:280px;">
<img src="{{flyer_url}}" alt="Event Flyer" style="width:100%;border-radius:16px;box-shadow:0 8px 30px rgba(0,0,0,0.1);display:block;" />
</div>
</div>
</div>
<div style="background:#0f172a;padding:20px 24px;text-align:center;">
<p style="font-size:0.85rem;color:rgba(255,255,255,0.6);margin:0;">RSVP Deadline: <span style="color:#06b6d4;font-weight:600;">{{rsvp_deadline}}</span></p>
</div>
</div>`
    },
    {
        id: 'split-hero',
        name: 'Split Hero',
        style: 'Side-by-Side',
        previewStyle: { background: '#fff' },
        previewHtml: '<div style="display:flex;height:100%;"><div style="flex:1;background:linear-gradient(135deg,#1e3a5f,#0f172a);padding:10px;display:flex;align-items:center;justify-content:center;"><div style="text-align:center;"><div style="font-size:8px;font-weight:700;color:#fff;">EVENT</div><div style="font-size:5px;color:rgba(255,255,255,0.6);">Date</div></div></div><div style="flex:1;padding:10px;display:flex;align-items:center;justify-content:center;"><div style="text-align:center;"><div style="font-size:5px;color:#888;">You\'re Invited</div><div style="font-size:8px;font-weight:700;color:#333;">Guest</div><div style="display:flex;justify-content:center;gap:2px;margin-top:4px;"><div style="background:#22c55e;padding:1px 3px;border-radius:1px;font-size:4px;color:#fff;">Yes</div><div style="border:1px solid #ef4444;padding:1px 3px;border-radius:1px;font-size:4px;color:#ef4444;">No</div></div></div></div></div>',
        html: `<div style="min-height:100vh;display:flex;flex-wrap:wrap;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow-x:hidden;">
<div style="flex:1;min-width:320px;background:linear-gradient(135deg,rgba(15,23,42,0.85),rgba(30,58,95,0.9)),url('{{flyer_url}}') center/cover no-repeat;color:#fff;display:flex;align-items:center;justify-content:center;padding:60px 40px;">
<div style="text-align:center;max-width:450px;">
<p style="font-size:0.75rem;text-transform:uppercase;letter-spacing:5px;color:rgba(255,255,255,0.5);margin:0 0 24px;">{{company_name}}</p>
<h1 style="font-size:clamp(2rem,4vw,3rem);font-weight:800;margin:0 0 20px;line-height:1.2;">{{event_name}}</h1>
<div style="width:50px;height:3px;background:#3b82f6;margin:0 auto 20px;border-radius:2px;"></div>
<p style="font-size:1rem;color:rgba(255,255,255,0.7);margin:0 0 8px;">{{event_date}}</p>
<p style="font-size:1rem;color:rgba(255,255,255,0.7);margin:0 0 8px;">to {{event_end_date}}</p>
<p style="font-size:0.95rem;color:rgba(255,255,255,0.5);margin:0;">{{venue}} &bull; {{address}}</p>
</div>
</div>
<div style="flex:1;min-width:320px;background:#fff;display:flex;align-items:center;justify-content:center;padding:60px 40px;">
<div style="max-width:450px;width:100%;">
<p style="font-size:0.85rem;text-transform:uppercase;letter-spacing:4px;color:#94a3b8;margin:0 0 12px;">You're Invited</p>
<h2 style="font-size:clamp(1.8rem,3.5vw,2.8rem);font-weight:800;color:#111;margin:0 0 8px;">{{invitee_name}}</h2>
<p style="font-size:0.9rem;color:#94a3b8;margin:0 0 24px;">{{invitee_email}}</p>
<div style="width:40px;height:3px;background:#3b82f6;margin:0 0 24px;border-radius:2px;"></div>
<p style="font-size:0.95rem;line-height:1.8;color:#555;margin:0 0 32px;">{{event_description}}</p>
<div data-rsvp-action style="padding:28px 0;text-align:center;">
<p style="font-size:1rem;color:#cbd5e1;margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
<div style="border-top:1px solid #e5e7eb;padding-top:20px;margin-top:12px;">
<p style="font-size:0.8rem;color:#94a3b8;margin:0;">Please respond by <span style="color:#3b82f6;font-weight:600;">{{rsvp_deadline}}</span></p>
</div>
</div>
</div>
</div>`
    },
    {
        id: 'vibrant-festival',
        name: 'Vibrant Festival',
        style: 'Warm / Energetic',
        previewStyle: { background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 30%, #f39c12 100%)' },
        previewHtml: '<div style="text-align:center;padding:10px;display:flex;align-items:center;justify-content:center;height:100%;"><div style="background:#fff;border-radius:8px;padding:10px;width:85%;box-shadow:0 2px 8px rgba(0,0,0,0.1);"><div style="background:#eee;height:20px;border-radius:4px 4px 0 0;margin:-2px -2px 6px;display:flex;align-items:center;justify-content:center;"><span style="font-size:4px;color:#888;">IMAGE</span></div><div style="font-size:9px;font-weight:700;color:#333;">Event</div><div style="display:flex;justify-content:center;gap:3px;margin-top:4px;"><div style="background:#ee5a24;color:#fff;padding:1px 4px;border-radius:6px;font-size:4px;">Date</div><div style="background:#f39c12;color:#fff;padding:1px 4px;border-radius:6px;font-size:4px;">Venue</div></div></div></div>',
        html: `<div style="min-height:100vh;background:linear-gradient(135deg,#ff6b6b 0%,#ee5a24 30%,#f39c12 100%);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:40px 24px;overflow-x:hidden;position:relative;">
<div style="position:absolute;top:10%;left:5%;width:80px;height:80px;border-radius:50%;background:rgba(255,255,255,0.08);"></div>
<div style="position:absolute;top:20%;right:8%;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.06);"></div>
<div style="position:absolute;bottom:15%;left:10%;width:60px;height:60px;border-radius:50%;background:rgba(255,255,255,0.05);"></div>
<div style="position:absolute;bottom:30%;right:5%;width:100px;height:100px;border-radius:50%;background:rgba(255,255,255,0.04);"></div>
<div style="max-width:680px;margin:0 auto;position:relative;z-index:1;">
<div style="background:#fff;border-radius:24px;box-shadow:0 20px 60px rgba(0,0,0,0.15);overflow:hidden;">
<img src="{{flyer_url}}" alt="Event Banner" style="width:100%;height:280px;object-fit:cover;display:block;" />
<div style="padding:40px 36px;">
<h1 style="font-size:clamp(1.6rem,4vw,2.4rem);font-weight:800;color:#222;margin:0 0 16px;line-height:1.2;">{{event_name}}</h1>
<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:20px;">
<span style="display:inline-block;background:linear-gradient(135deg,#ff6b6b,#ee5a24);color:#fff;padding:6px 16px;border-radius:50px;font-size:0.85rem;font-weight:600;">{{event_date}}</span>
<span style="display:inline-block;background:linear-gradient(135deg,#f39c12,#e67e22);color:#fff;padding:6px 16px;border-radius:50px;font-size:0.85rem;font-weight:600;">{{venue}}</span>
</div>
<p style="font-size:0.9rem;color:#888;margin:0 0 20px;">{{address}}</p>
<p style="font-size:0.95rem;line-height:1.8;color:#555;margin:0 0 28px;">{{event_description}}</p>
<div style="background:linear-gradient(135deg,#fff5f5,#fffbeb);border-radius:16px;padding:24px;text-align:center;margin-bottom:28px;">
<p style="font-size:0.85rem;color:#888;margin:0 0 4px;">Hello there,</p>
<h3 style="font-size:1.4rem;font-weight:700;color:#ee5a24;margin:0;">{{invitee_name}}</h3>
</div>
<div data-rsvp-action style="text-align:center;padding:28px;background:#fafafa;border-radius:16px;margin-bottom:24px;">
<p style="font-size:1rem;color:#bbb;margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
<p style="text-align:center;font-size:0.85rem;color:#999;margin:0;">Respond by <span style="color:#ee5a24;font-weight:600;">{{rsvp_deadline}}</span></p>
</div>
</div>
</div>
</div>`
    },
    {
        id: 'clean-minimal',
        name: 'Clean Minimal',
        style: 'Apple-style',
        previewStyle: { background: '#ffffff', border: '1px solid #eee' },
        previewHtml: '<div style="text-align:center;padding:14px;"><div style="font-size:5px;color:#aaa;letter-spacing:1px;">COMPANY</div><div style="width:20px;height:1px;background:#eee;margin:4px auto;"></div><div style="font-size:12px;font-weight:800;color:#111;">Event Name</div><div style="font-size:5px;color:#888;margin-top:2px;">Date | Venue</div><div style="font-size:6px;color:#333;margin-top:6px;">Dear Guest</div><div style="width:16px;height:1px;background:#eee;margin:4px auto;"></div><div style="font-size:4px;color:#aaa;">RSVP</div></div>',
        html: `<div style="min-height:100vh;background:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow-x:hidden;">
<div style="max-width:650px;margin:0 auto;padding:80px 24px 60px;">
<p style="font-size:0.8rem;text-transform:uppercase;letter-spacing:4px;color:#aaa;margin:0 0 20px;text-align:center;">{{company_name}}</p>
<div style="width:40px;height:1px;background:#e5e7eb;margin:0 auto 40px;"></div>
<h1 style="font-size:clamp(2rem,5vw,3.5rem);font-weight:800;color:#111;margin:0 0 16px;text-align:center;line-height:1.15;letter-spacing:-0.02em;">{{event_name}}</h1>
<p style="font-size:1rem;color:#888;text-align:center;margin:0 0 40px;">{{event_date}} <span style="color:#ddd;">|</span> {{venue}}</p>
<img src="{{flyer_url}}" alt="Event" style="width:100%;border-radius:12px;margin-bottom:40px;display:block;" />
<p style="font-size:1.05rem;line-height:2;color:#444;margin:0 0 16px;">{{event_description}}</p>
<div style="margin:20px 0;">
<p style="font-size:0.9rem;color:#888;margin:0 0 4px;">{{event_end_date}}</p>
<p style="font-size:0.9rem;color:#888;margin:0;">{{address}}</p>
</div>
<div style="width:40px;height:1px;background:#e5e7eb;margin:40px 0;"></div>
<p style="font-size:1.1rem;color:#333;margin:0 0 6px;">Dear <span style="font-weight:700;">{{invitee_name}}</span>,</p>
<p style="font-size:0.95rem;color:#666;line-height:1.8;margin:0 0 36px;">We would love for you to join us at this event. Please let us know if you can make it.</p>
<div data-rsvp-action style="text-align:center;padding:32px 0;">
<p style="font-size:1rem;color:#ccc;margin:0;">RSVP Action Area - Accept/Decline buttons appear here</p>
</div>
<div style="width:40px;height:1px;background:#e5e7eb;margin:32px auto;"></div>
<p style="text-align:center;font-size:0.85rem;color:#aaa;margin:0;">Please respond by {{rsvp_deadline}}</p>
</div>
</div>`
    },
]

export default {
    name: 'RSVPLandingPageDesigner',
    mixins: [popupMixins],
    components: { PageBuilder },
    data() {
        return {
            workshopId: null,
            saving: false,
            showTemplateGallery: true,
            showPreview: false,
            editorHtml: '',
            currentHtml: '',
            templates: RSVP_TEMPLATES,
            additionalFieldNames: [],
        }
    },
    computed: {
        templateVars() {
            const base = ['event_name', 'event_date', 'event_end_date', 'venue', 'address', 'event_description', 'rsvp_deadline', 'flyer_url', 'invitee_name', 'invitee_email', 'company_name']
            return [...base, ...this.additionalFieldNames]
        },
        renderedPreview() {
            let html = this.currentHtml || this.editorHtml
            if (!html) return ''
            const el = document.createElement('textarea')
            el.innerHTML = html
            html = el.value
            const sampleData = {
                event_name: 'Innovate 10X Leadership Summit 2026',
                event_date: 'Saturday, March 15, 2026 at 9:00 AM',
                event_end_date: 'Sunday, March 16, 2026 at 5:00 PM',
                venue: 'Kingston Convention Centre',
                address: '12 Ocean Boulevard, Kingston, Jamaica',
                event_description: 'Join industry leaders for two days of transformational workshops, keynote speakers, and networking. Featuring 20+ speakers across 4 tracks.',
                rsvp_deadline: 'March 10, 2026',
                flyer_url: 'https://placehold.co/1200x600/1a1a2e/ffffff?text=EVENT+BANNER',
                invitee_name: 'John Smith',
                invitee_email: 'john.smith@example.com',
                company_name: 'Innovate 10X',
            }
            this.additionalFieldNames.forEach(name => {
                const label = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                sampleData[name] = `[${label}]`
            })
            Object.keys(sampleData).forEach(key => {
                html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), sampleData[key])
            })
            html = html.replace(
                /<div[^>]*data-rsvp-action[^>]*>[\s\S]*?<\/div>/gi,
                `<div style="text-align:center;padding:24px 0;"><button style="background:#22c55e;color:#fff;padding:16px 52px;border:none;border-radius:8px;font-size:1.1rem;font-weight:700;cursor:pointer;margin:0 8px;box-shadow:0 4px 15px rgba(34,197,94,0.4);">&#10003; Accept</button><button style="background:transparent;color:#ef4444;padding:16px 52px;border:2px solid #ef4444;border-radius:8px;font-size:1.1rem;font-weight:700;cursor:pointer;margin:0 8px;">&#10005; Decline</button></div>`
            )
            return html
        },
    },
    created() {
        this.workshopId = this.$route.params.workshopId
        this.loadExistingDesign()
    },
    methods: {
        decodeHtmlEntities(str) {
            const el = document.createElement('textarea')
            el.innerHTML = str
            return el.value
        },
        async loadExistingDesign() {
            try {
                const response = await axios.get(v2Admin(`workshops/${this.workshopId}/invitations/design?type=rsvp_landing_page`))
                const data = response.data?.success?.data || {}
                let html = data.html || ''
                this.additionalFieldNames = data.additional_field_names || []
                if (html) {
                    html = this.decodeHtmlEntities(html)
                    this.editorHtml = html
                    this.currentHtml = html
                    this.showTemplateGallery = false
                }
            } catch (err) {
                // No saved design yet
            }
        },
        selectTemplate(tpl) {
            this.editorHtml = tpl.html
            this.currentHtml = tpl.html
            this.showTemplateGallery = false
        },
        startFromScratch() {
            this.editorHtml = '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8f9fa;font-family:sans-serif;text-align:center;padding:40px;"><div><h1>{{event_name}}</h1><p>{{event_date}} - {{venue}}</p><p>Hello {{invitee_name}}!</p><div data-rsvp-action style="padding:24px;"><p>RSVP Action Area</p></div></div></div>'
            this.currentHtml = this.editorHtml
            this.showTemplateGallery = false
        },
        onEditorChange(html) {
            this.currentHtml = html
        },
        previewDesign() {
            if (this.$refs.editor) {
                this.currentHtml = this.$refs.editor.getHtml()
            }
            this.showPreview = true
        },
        async saveDesign() {
            try {
                this.saving = true
                const html = this.$refs.editor ? this.$refs.editor.getHtml() : (this.currentHtml || this.editorHtml)
                await axios.post(v2Admin(`workshops/${this.workshopId}/invitations/design`), {
                    type: 'rsvp_landing_page',
                    html,
                })
                this.doToast('Success', 'RSVP landing page design saved', 'success')
            } catch (err) {
                this.doToast('Error', err.response?.data?.error?.message || 'Failed to save design', 'danger')
            } finally {
                this.saving = false
            }
        },
        goBack() {
            this.$router.push({ name: 'EventInvitationList', params: { workshopId: this.workshopId } })
        },
    },
}
</script>

<style scoped>
.modern-dashboard-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}
.card-header-modern {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    padding: 20px 25px;
}
.header-icon-box {
    width: 45px;
    height: 45px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
}
.text-white-op-70 { color: rgba(255, 255, 255, 0.7); }
.card-body-modern { padding: 0; }

.template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
}
.template-card {
    border: 2px solid #f1f3f5;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s;
}
.template-card:hover {
    border-color: #e74c3c;
    box-shadow: 0 4px 12px rgba(231, 76, 60, 0.15);
    transform: translateY(-2px);
}
.template-preview {
    height: 140px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.template-preview-content {
    transform: scale(0.8);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.template-info {
    border-top: 1px solid #f1f3f5;
    background: #fafafa;
}

.editor-container {
    min-height: 600px;
}
.editor-container ::v-deep #gjs {
    min-height: 550px;
}
.btn-pill {
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.85rem;
}

.preview-frame {
    min-height: 500px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: auto;
}
</style>
